// import Promise from 'bluebird';
import Immutable from "immutable";
import uuid from "uuid";
import includes from "lodash/includes";
import difference from "lodash/difference";
import actions from "redux-form/lib/actions";
import { CHANGE, FOCUS, BLUR, ARRAY_PUSH, ARRAY_REMOVE } from "redux-form/lib/actionTypes";
import deleteIn from "redux-form/lib/structure/plain/deleteIn";
import isObject from "lodash/isObject";
import toPath from "lodash/toPath";
import $ from "jquery";

import api from "../../lib/api";

import "../../lib/storage";
import { getCartData, getCartContextData } from "../../lib/utils";
import calculateCart from "./cart/calc";
import { getStore } from "../../store";
import { CARD_ID_RIBBON } from "../../lib/constants";

// ------------------------------------
// Actions
// ------------------------------------

export const HYDRATE = "@app/cart/HYDRATE";
export const HYDRATE_CONTEXT = "@app/cart/HYDRATE_CONTEXT";
export const HYDRATE_SCHEMA = "@app/cart/HYDRATE_SCHEMA";
export const INITIALIZE = "@app/cart/INITIALIZE";
export const HYDRATE_POSTCODE = "@app/cart/HYDRATE_POSTCODE";
export const SET_ACTIVE_STEP = "@app/cart/SET_ACTIVE_STEP";
export const ADD_ITEM = "@app/cart/ADD_ITEM";
export const ADD_ITEM_EXTRA_PRODUCT = "@app/cart/ADD_ITEM_EXTRA_PRODUCT";
export const CALCULATE = "@app/cart/CALCULATE";
export const SUBMIT = "@app/cart/SUBMIT";
export const REMOVE_ITEM = "@app/cart/REMOVE_ITEM";
export const REMOVE_ITEM_EXTRA_PRODUCT = "@app/cart/REMOVE_ITEM_EXTRA_PRODUCT";
export const SET_PROPERTY = "@app/cart/SET_PROPERTY";

export const setProperty = (path, value) => ({
  type: SET_PROPERTY,
  payload: { path, value }
});

export const submitCart = data => ({
  type: SUBMIT,
  payload: api.post("/cart/order").send(data)
});

export const calculate = () => (dispatch, getState) =>
  dispatch({
    type: CALCULATE,
    payload: calculateCart(getState())
  });

export const createInitialRecipient = countryCode => ({
  uuid: uuid(),
  address: {
    countryCode: countryCode || "NL",
    addressType: "residence"
  },
  products: [],
  delivery: {
    messageType: 1
  },
  card: {
    type: 3826527 // blank card
  }
});

const hydrateCartData = () =>
  getCartData({
    items: [],
    customer: {
      type: "person",
      countryCode: "NL"
    },
    recipients: [createInitialRecipient()],
    checkout: {
      payment: {
        type: 0
      },
      termsAndConditions: false
    }
  });

const contextData = getCartContextData({
  webProduct: {
    uuid: uuid(),
    webProductCollection: {}
  }
});

const cartData = hydrateCartData();
window.hydrateCartData = hydrateCartData;

// ------------------------------------
// Action generators
// ------------------------------------

export const hydrate = () => ({
  type: HYDRATE,
  payload: Promise.resolve(hydrateCartData())
});

export const hydratePostcode = params => (dispatch, getState) =>
  dispatch({
    type: HYDRATE_POSTCODE,
    payload: api
      .get("/cart/postcode")
      .query({
        ...params,
        id: getState().cart.getIn(
          ["context", "webProduct", "id"],
          getState().cart.getIn(["context", "currentTime"], "")
        ),
        token: getState().cart.getIn(["context", "csrftok"], "K")
      })
      .cashier()
      .lifo()
  });

export const hydrateContext = cart => ({
  type: HYDRATE_CONTEXT,
  payload: api
    .post("/cart/context", cart)
    .cashier()
    .lifo()
    .then(context => {
      context.recipientContexts.forEach((recipientContext, idx) => {
        if (recipientContext.availableTimeFrames && recipientContext.availableTimeFrames.length === 1) {
          getStore().dispatch(
            actions.change("cart", `recipients.${idx}.delivery.timeFrame`, recipientContext.availableTimeFrames[0].id)
          );
        }
      });
      return context;
    })
});

export const formClearSyncError = (form, field) => ({
  type: "FORM_CLEAR_SYNC_ERROR",
  payload: { form, field }
});

export const formClearSyncErrors = form => ({
  type: "FORM_CLEAR_SYNC_ERRORS",
  payload: { form }
});

export const formClearAsyncErrors = form => ({
  type: "FORM_CLEAR_ASYNC_ERRORS",
  payload: { form }
});

export const hydrateSchema = () => ({
  type: HYDRATE_SCHEMA,
  payload: api
    .get("/cart/schema")
    .cashier()
    .lifo()
});

export const initialize = () => dispatch =>
  dispatch({
    type: INITIALIZE,
    payload: dispatch(hydrate()).then(cart =>
      Promise.all([dispatch(hydrateSchema(cart)), dispatch(hydrateContext(cart))])
    )
  });

export const setActiveStep = step => ({
  type: SET_ACTIVE_STEP,
  payload: step
});

// ------------------------------------
// Default State
// ------------------------------------

const State = Immutable.Record({
  isInitializing: false,
  hasInitialized: false,
  isFirstShow: true,
  activeStep: 1,
  isFetching: false,
  hasFetched: false,
  error: null,
  data: cartData,
  totals: Immutable.Map(),
  isFetchingPostcode: false,
  hasFetchedPostcode: false,
  postcodeError: null,
  postcode: Immutable.Map(),
  isFetchingContext: false,
  hasFetchedContext: true,
  contextError: null,
  context: contextData,
  isFetchingSchema: false,
  hasFetchedSchema: false,
  schemaError: null,
  schema: Immutable.Map(),
  settings: Immutable.Map()
});

// ------------------------------------
// Action Handlers
// ------------------------------------

const updateRecipientsProducts = cart => {
  cart = Immutable.fromJS(cart);

  const items = cart.get("items");
  const itemIds = items.reduce((carry, item) => {
    carry.push(item.get("uuid"));
    return carry;
  }, []);

  const recipients = cart.get("recipients");

  const recipientCount = recipients.size;

  return cart.set(
    "recipients",
    recipients.map((r, idx) => {
      const initRecipient = idx === 0;

      // if (items.size === 0) {
      //   r = r
      //     .setIn(['delivery', 'date'], '')
      //     .setIn(['delivery', 'timeFrame'], '');
      // }

      // remove unknown products
      r = r.set("products", r.get("products").filter(p => includes(itemIds, p.get("uuid"))));

      if (initRecipient && recipientCount === 1) {
        // r = product.set('quantity', item.get('quantity'));
        r = r.set(
          "products",
          items.reduce((products, item) => {
            products = products.push(
              Immutable.Map({
                uuid: item.get("uuid"),
                quantity: initRecipient ? item.get("quantity") : 0,
                maxQuantity: item.get("quantity"),
                webProductId: item.get("webProductId"),
                productCollectionMemberId: item.get("productCollectionMemberId")
              })
            );
            return products;
          }, new Immutable.List())
        );
      }

      const needIds = difference(
        itemIds,
        r.get("products").reduce((carry, item) => {
          carry.push(item.get("uuid"));
          return carry;
        }, [])
      );

      if (__DEV__) {
        // eslint-disable-next-line no-console
        console.log("needIds", needIds);
      }
      if (!needIds.length) {
        return r;
      }

      const newItems = items.filter(i => includes(needIds, i.get("uuid")));

      r = r.set(
        "products",
        newItems.reduce((products, item) => {
          products = products.push(
            Immutable.Map({
              uuid: item.get("uuid"),
              quantity: initRecipient ? item.get("quantity") : 0,
              maxQuantity: item.get("quantity"),
              webProductId: item.get("webProductId"),
              productCollectionMemberId: item.get("productCollectionMemberId")
            })
          );
          return products;
        }, r.get("products"))
      );

      return r;
    })
  );
};

const ACTION_HANDLERS = {
  [SET_PROPERTY]: (state, { payload }) => state.setIn(["settings"].concat(toPath(payload.path)), payload.value),
  [CALCULATE]: (state, { status, payload }) => {
    if (status !== "success") {
      return state;
    }
    return state.set("totals", payload);
  },
  [SET_ACTIVE_STEP]: (state, { payload }) =>
    state.set("isFirstShow", state.get("isFirstShow") && payload === 2).set("activeStep", payload),
  [INITIALIZE]: (state, { status, payload }) => {
    switch (status) {
      case "pending":
        return state.set("isInitializing", true);
      case "success":
        return state.set("isInitializing", false).set("hasInitialized", true);
      case "error":
        return state.set("isInitializing", false).set("error", Immutable.fromJS(payload));
      default:
        return state;
    }
  },
  [HYDRATE]: (state, { status, payload }) => {
    switch (status) {
      case "pending":
        return state.set("isFetching", true);
      case "success":
        return state
          .set("isFetching", false)
          .set("hasFetched", true)
          .set("data", payload);
      case "error":
        return state.set("isFetching", false).set("error", Immutable.fromJS(payload));
      default:
        return state;
    }
  },
  [HYDRATE_POSTCODE]: (state, { status, payload }) => {
    switch (status) {
      case "pending":
        return state.set("isFetchingPostcode", true);
      case "success":
        return state
          .set("isFetchingPostcode", false)
          .set("hasFetchedPostcode", true)
          .set("postcode", Immutable.fromJS(payload));
      case "error":
        return state.set("isFetchingPostcode", false).set("postcodeError", Immutable.fromJS(payload));
      default:
        return state;
    }
  },
  [HYDRATE_CONTEXT]: (state, { status, payload }) => {
    switch (status) {
      case "pending":
        return state.set("isFetchingContext", true);
      case "success":
        return state
          .set("isFetchingContext", false)
          .set("hasFetchedContext", true)
          .set("context", state.get("context").merge(Immutable.fromJS(payload)));
      case "error":
        return state.set("isFetchingContext", false).set("contextError", Immutable.fromJS(payload));
      default:
        return state;
    }
  },
  [HYDRATE_SCHEMA]: (state, { status, payload }) => {
    switch (status) {
      case "pending":
        return state.set("isFetchingSchema", true);
      case "success":
        return state
          .set("isFetchingSchema", false)
          .set("hasFetchedSchema", true)
          .set("schema", Immutable.fromJS(payload));
      case "error":
        return state.set("isFetchingSchema", false).set("schemaError", Immutable.fromJS(payload));
      default:
        return state;
    }
  }
};

// ------------------------------------
// Reducer
// ------------------------------------

export default function reducer(state = new State(), action) {
  const handler = ACTION_HANDLERS[action.type];
  // window.cartStorage.setItem('cart-data', JSON.stringify(state.get('data').toJS()));
  return handler ? handler(state, action) : state;
}

export const cartFormReducer = (state, action) => {
  if (!state || !state.values) {
    return state;
  }

  if (action.type === "FORM_CLEAR_SYNC_ERROR") {
    return deleteIn(state, `asyncErrors.${action.field}`);
  }

  if (action.type === "FORM_CLEAR_SYNC_ERRORS") {
    return deleteIn(state, "syncErrors");
  }

  if (action.type === "FORM_CLEAR_ASYNC_ERRORS") {
    return deleteIn(state, "syncErrors");
  }

  if ((action.type === ARRAY_PUSH || action.type === ARRAY_REMOVE) && action.meta.field === "items") {
    // console.log('ARRAY_PUSH.item', action.payload);
    state.values = updateRecipientsProducts(state.values).toJS();
  }

  if (action.type === CHANGE && /items\[\d+]\.(quantity|productCollectionMemberId)/.test(action.meta.field)) {
    const item = state.values.items[parseInt(action.meta.field.match(/items\[(\d+)]/)[1], 10)];
    // console.log('CHANGE.item', item);
    if (item && item.productCollectionMemberId) {
      state.values = updateRecipientsProducts(state.values).toJS();
      window.setTimeout(() => {
        $(window).trigger("debloemist.cart.updated");
      }, 1);
    }
  }

  if ((action.type === FOCUS || action.type === CHANGE) && action.meta.field.indexOf("customPrice") !== -1) {
    const index = parseInt(action.meta.field.match(/items\[(\d+)]/)[1], 10);
    if (!Number.isNaN(index)) {
      state.values.items[index].productCollectionMemberId = 0;
    }
  }

  if ((action.type === CHANGE || action.type === BLUR) && /recipients\[\d+]\.card\.type/.test(action.meta.field)) {
    const index = parseInt(action.meta.field.match(/recipients\[(\d+)]/)[1], 10);
    if (action.payload === CARD_ID_RIBBON && !isObject(state.values.recipients[index].card.text)) {
      // from CARD to RIBBON
      state.values.recipients[index].card.text = {
        left: (state.values.recipients[index].card.text || "").substring(0, 50),
        right: ""
      };
    }

    if (action.payload !== CARD_ID_RIBBON) {
      // from RIBBON to CARD
      if (state.values.recipients[index].card && isObject(state.values.recipients[index].card.text)) {
        const left = state.values.recipients[index].card.text.left || "";
        const right = state.values.recipients[index].card.text.right || "";
        if (left || right) {
          state.values.recipients[index].card.text = `${left ? `${left} ` : ""}${right}`;
        } else {
          state.values.recipients[index].card.text = "";
        }
      }
    }
  }

  if (/(BLUR|CHANGE|ARRAY_)/.test(action.type) && window.cartStorage) {
    window.cartStorage.setItem("cart-data", JSON.stringify(state.values));
  }

  return state;
};
